import React, {useCallback, useEffect} from 'react';
import {Box} from '@chakra-ui/react';
import {TPath, useTypedQuery} from '@synako/typed-path';
import {
  FiltresItemCatalogueGrandCompte,
  coerceFiltresItemCatalogueGrandCompte as coerce,
} from 'ff-common';
import {useApi} from '@/haligator-factory';
import {useHalState} from '@synako/haligator';
import {CategoryFiltersBar} from './components/CategoryFiltersBar';
import {FilterOptionsBar} from './components/FilterOptionsBar';
import {SearchResults} from './components/SearchResults';
import {CatalogueGrandCompteHeader} from './components/CatalogueGrandCompteHeader';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useTranslation} from 'react-i18next';

/**
 * Composant principal pour la recherche dans le catalogue grand compte
 */
export const CatalogueGrandCompteSearch: React.FC<{backoffice?: boolean}> = ({
  backoffice = false,
}) => {
  // Stockage des filtres dans l'URL
  const filtersHook =
    useTypedQuery<TPath<{Query: FiltresItemCatalogueGrandCompte}>>(coerce);
  const textToast = useSimpleTextToast();
  const {t} = useTranslation();

  // Constante pour la limite de résultats par page
  const RESULTS_LIMIT = 30;

  // Récupération des résultats de recherche
  const match = useHalState(
    useApi().follow('get-search_catalogue', {
      ...filtersHook.params,
      search: filtersHook.params.q,
      est_TAC: filtersHook.params.est_TAC,
      est_MP: filtersHook.params.est_MP,
      nature_du_prix: filtersHook.params.nature_du_prix,
      labels: filtersHook.params.labels,
      temperature_livraison: filtersHook.params.temperature_livraison,
      page: filtersHook.params.page ?? 1,
      limit: RESULTS_LIMIT,
    }),
  );

  // Réinitialise la page à 1 lorsque les filtres changent
  useEffect(() => {
    if (filtersHook.params.page && filtersHook.params.page !== 1) {
      filtersHook.change('page', 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersHook.params.q,
    filtersHook.params.cat_web,
    filtersHook.params.est_TAC,
    filtersHook.params.est_MP,
    filtersHook.params.nature_du_prix,
    filtersHook.params.labels,
    filtersHook.params.temperature_livraison,
  ]);

  useEffect(() => {
    if (match.error) {
      const errorCode = match.error.message;
      textToast(t(`error.${errorCode}`), {icon: 'alert'});
    }
  }, [match.error, textToast, t]);

  const isLoading = match.loading || match.refreshing;
  const hits = match.data?.hits;
  const paginationData = match.data?.pagination;
  const followArticle = useCallback(
    (code_article: string) => match.state?.follow('go:article', {code_article}),
    [match.state],
  );

  return (
    <Box
      bg='white'
      width='100%'
      overflowX='hidden'
    >
      {/* Header avec logo, search et user */}
      <CatalogueGrandCompteHeader
        backoffice={backoffice}
        filtersHook={filtersHook}
      />

      {/* Section filtres de catégorie */}
      <CategoryFiltersBar
        filtersHook={filtersHook}
        facets={match.data?.facets}
      />

      {/* Section des options de filtrage */}
      <FilterOptionsBar
        filtersHook={filtersHook}
        facets={match.data?.facets}
        pagination={paginationData}
        backoffice={backoffice}
      />

      {/* Contenu principal - résultats de recherche */}
      <SearchResults
        isLoading={isLoading}
        hits={hits}
        paginationData={paginationData}
        followArticle={followArticle}
        filtersHook={filtersHook}
        backoffice={backoffice}
        onAjouterArticle={() => {
          // Fonction vide par défaut pour le catalogue grand compte
        }}
      />
    </Box>
  );
};
